import React, {useEffect} from 'react';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ConfiguratorResultType} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  FlowchartEditorLoadError,
  FlowchartTaskOrSchemaInaccessibleError,
} from 'processes/modals/ProcessFlowchartModalShared';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {useAllSchemasByIdWithSingletonSchemas} from '../useAllSchemasById';
import {createInitialExecutionDraftState} from './createInitialExecutionDraftState';
import {useExecutionEditorConfigurator} from './executionFlowchartConfigurator';
import {FlowchartDataResultType, useFlowchartConfigData} from './executionFlowchartData';
import ExecutionFlowchartEditor from './ExecutionFlowchartEditor';
import {ACMExecutionFlowchartPaneProps} from './ExecutionFlowchartPaneTypes';

export default function ACMExecutionFlowchartPane(props: ACMExecutionFlowchartPaneProps): JSX.Element {
  const {draftState, setDraftState} = props;
  const {configuratorResult, executionEditorConfigurator} = useExecutionEditorConfigurator(props.editorMode);
  const dataResult = useFlowchartConfigData(props.flowchartConfigId, executionEditorConfigurator, null);
  const data = dataResult.resultType === FlowchartDataResultType.SUCCESS ? dataResult.data : null;

  const needsSidepanel =
    executionEditorConfigurator?.hasFeature(ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL) ??
    false;

  const {
    schemasById: allSchemasById,
    processTaskDefinitionsResult: taskDefinitionsResult,
    singletonTaskSchemasByName,
  } = useAllSchemasByIdWithSingletonSchemas({
    flowchartEditorConfigurator: executionEditorConfigurator,
    rootTaskSchema: data?.rootTaskSchema ?? null,
    skipTaskDefinitionsQuery: !needsSidepanel,
    availableTaskSchemas: data?.availableTaskSchemas,
  });

  useEffect(() => {
    if (data == null || executionEditorConfigurator == null || draftState != null) {
      return;
    }
    // TODO: PR 229196 have the parent initialize
    setDraftState(createInitialExecutionDraftState(data, props.editorMode, executionEditorConfigurator));
  }, [data, draftState, executionEditorConfigurator, props.editorMode, setDraftState]);

  if (
    configuratorResult.resultType === ConfiguratorResultType.ERROR ||
    dataResult.resultType === FlowchartDataResultType.ERROR
  ) {
    return <FlowchartEditorLoadError />;
  }

  if (dataResult.resultType === FlowchartDataResultType.INACCESSIBLE) {
    return <FlowchartTaskOrSchemaInaccessibleError />;
  }

  if (executionEditorConfigurator == null || data == null || allSchemasById == null || draftState == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <ExecutionFlowchartEditor
      editorMode={props.editorMode}
      data={data}
      executionEditorConfigurator={executionEditorConfigurator}
      draftState={draftState}
      setDraftState={setDraftState}
      onBlockingErrorsChange={props.onBlockingErrorsChange}
      allSchemasById={allSchemasById}
      taskDefinitionsResult={taskDefinitionsResult}
      singletonTaskSchemasByName={singletonTaskSchemasByName}
    />
  );
}
